import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import { useField, useFormikContext } from 'formik';
import { Grid, Icon, Popup, Button } from 'semantic-ui-react';
import { useSelector } from 'react-redux';
import { getResourceUrl, extractParamsFromPath } from '../utils/pathRegistryUtils';
import { ensureSafeFilename } from '../utils/fileUtils';
import defaultAvatar from '../assets/shutterstock_2294366549-480x480.png'; // Default avatar image

// Debug flag to control console logging
const DEBUG = false;

// Debug logging function
const debugLog = (...args) => {
  if (DEBUG) {
    console.log(...args);
  }
};

// Memoize the ReadOnlyField component to prevent unnecessary re-renders
const ReadOnlyField = memo(({ error, value, ...props }) => {
  // Styling for the read-only field
  const fieldStyle = {
    width: '480px', // Set maximum width to 500px
    maxWidth: '480px', // Set maximum width to 500px
    padding: '0.67857143em 1em',
    border: error ? '1px solid #e0b4b4' : '1px solid rgba(34,36,38,.15)',
    borderRadius: '.28571429rem',
    backgroundColor: error ? '#fff6f6' : '#f9f9f9',
    color: value ? '#000' : '#767676',
    cursor: 'default',
    whiteSpace: 'nowrap',      // Prevent text from wrapping
    overflow: 'hidden',        // Hide overflow
    textOverflow: 'ellipsis',  // Show ellipsis for overflow
    ...props.style
  };

  return (
    <div style={{ marginBottom: '1em' }}>
      <div style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        maxWidth: '470px'  // Also constrain the container
      }}>
        <div
          style={fieldStyle}
          title={value} // Add title attribute to show full text on hover
        >
          {value || 'No file selected. Click or drag an image to upload.'}
        </div>
      </div>
    </div>
  );
});

const FormikImageUpload = ({
  urlFieldName = 'profileImageUrl',
  fileFieldName = 'profileImageFile',
  flagFieldName = 'hasNewImageFile',
  resourceType, // Required prop for specifying the resource type
  label,
  disabled = false,
  ...props
}) => {
  // Use multiple fields instead of one overloaded field
  const [urlField, /* urlMeta */, urlHelpers] = useField(urlFieldName);
  const [fileField, /* fileMeta */, fileHelpers] = useField(fileFieldName);
  const [flagField, /* flagMeta */, flagHelpers] = useField(flagFieldName);
  const formik = useFormikContext();
  const { errors, touched } = formik;

  // Get the user ID from the Redux store
  const userId = useSelector((state) => state.user.profile?.data?.userId);
  // Track if we've attempted to fetch the signed URL
  const [fetchAttempted, setFetchAttempted] = useState(false);

  // State for the preview image
  const [previewUrl, setPreviewUrl] = useState(null);
  // State for the file object
  const [imageFile, setImageFile] = useState(null);
  // State for the displayed file name
  const [fileName, setFileName] = useState('');
  // State for the signed URL
  const [signedUrl, setSignedUrl] = useState(null);
  // State for image loading
  const [imageLoading, setImageLoading] = useState(false);
  // State for error messages
  const [errorMessage, setErrorMessage] = useState('');

  // Check if any of our fields have errors
  const hasError = (touched[urlFieldName] && errors[urlFieldName]) ||
    (touched[fileFieldName] && errors[fileFieldName]) ||
    (touched[flagFieldName] && errors[flagFieldName]) ||
    errorMessage;
  // Cleanup function for blob URLs
  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        debugLog('Revoking blob URL:', previewUrl);
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Initialize from field values
  useEffect(() => {
    debugLog('FormikImageUpload urlField value:', urlField.value);
    debugLog('FormikImageUpload fileField value:', fileField.value);
    debugLog('FormikImageUpload flagField value:', flagField.value);

    // If we have a new image file (flag is true)
    if (flagField.value && fileField.value) {
      debugLog('We have a new image file');
      setImageFile(fileField.value);
      setFileName(fileField.value.name);

      // Create a preview URL for the file if we don't already have one
      if (!previewUrl && fileField.value instanceof File) {
        const newPreviewUrl = URL.createObjectURL(fileField.value);
        debugLog('Created new preview URL:', newPreviewUrl);
        setPreviewUrl(newPreviewUrl);
      }
    }
    // If we have a URL but no new file
    else if (urlField.value && typeof urlField.value === 'string' && urlField.value.trim() !== '') {
      debugLog('We have a URL but no new file:', urlField.value);

      // Extract filename from URL if possible
      try {
        const url = new URL(urlField.value);
        debugLog('Parsed URL:', url);
        const pathParts = url.pathname.split('/');
        debugLog('Path parts:', pathParts);
        const lastPart = pathParts[pathParts.length - 1];
        debugLog('Last part (filename):', lastPart);
        setFileName(lastPart || 'Image from URL');
      } catch (e) {
        debugLog('Error parsing URL:', e);
        setFileName('Image from URL');
      }
    }
    // If we have neither
    else {
      debugLog('No field values, resetting state');
      setPreviewUrl(null);
      setImageFile(null);
      setFileName('');
      setSignedUrl(null);
    }

    // Log the current state after the effect runs
    debugLog('Current state - previewUrl:', previewUrl);
    debugLog('Current state - imageFile:', imageFile);
    debugLog('Current state - fileName:', fileName);
  }, [urlField.value, fileField.value, flagField.value, previewUrl, imageFile, fileName]);

  // Fetch signed URL when urlField.value changes and is a string URL
  useEffect(() => {
    const fetchSignedUrl = async () => {
      debugLog('Checking if we need to fetch signed URL...');
      debugLog('userId:', userId);
      debugLog('urlField.value:', urlField.value);
      debugLog('flagField.value:', flagField.value);
      debugLog('resourceType:', resourceType);

      // Skip if userId is not available
      if (!userId) {
        debugLog('No userId available, cannot fetch signed URL');
        setFetchAttempted(true);
        return;
      }

      // Skip if we have a new image file (flag is true)
      if (flagField.value) {
        debugLog('We have a new image file, skipping signed URL fetch');
        return;
      }

      if (typeof urlField.value !== 'string' || !urlField.value.trim()) {
        debugLog('urlField.value is not a valid string, skipping signed URL fetch');
        return;
      }

      // Check if this is an S3 URL
      const isS3Url = urlField.value.includes('s3.') ||
        urlField.value.includes('amazonaws.com') ||
        urlField.value.startsWith('https://aai-api');
      debugLog('Is S3 URL:', isS3Url);

      if (isS3Url) {
        debugLog('Fetching signed URL for:', urlField.value);
        setImageLoading(true);
        try {
          // Validate resourceType is provided
          if (!resourceType) {
            console.error('[FormikImageUpload] resourceType prop is required');
            setImageLoading(false);
            return;
          }

          // Parse the URL to get the path
          let path = '';
          try {
            const url = new URL(urlField.value);
            path = url.pathname;
            if (path.startsWith('/')) {
              path = path.substring(1); // Remove leading slash
            }
            debugLog('Parsed path from URL:', path);
          } catch (e) {
            console.error('[FormikImageUpload] Error parsing URL:', e);
            path = urlField.value; // Use the value as-is if it's not a valid URL
            debugLog('Using urlField.value as path:', path);
          }

          // Extract parameters using the Path Registry
          const extractionResult = await extractParamsFromPath(path, resourceType);

          let url = urlField.value; // Default to using the original URL

          // If parameter extraction was successful, use getResourceUrl
          if (extractionResult.success) {
            // The filename should be defined in the Path Registry
            // It's typically stored in the 'fileName' parameter
            const actualFileName = extractionResult.params.fileName;

            debugLog(`Using Path Registry for ${resourceType} with params:`, extractionResult.params);
            debugLog('Extracted filename from Path Registry:', actualFileName);

            try {
              // Use the extracted parameters and filename
              url = await getResourceUrl(
                userId,
                resourceType,
                extractionResult.params,
                actualFileName
              );
              debugLog('Got signed URL from Path Registry:', url);
            } catch (error) {
              console.error(`[FormikImageUpload] Error getting signed URL for ${resourceType}:`, error);
              // Fall back to using the original URL
              debugLog('Using original URL due to error');
              url = urlField.value;
            }
          } else {
            console.error(`[FormikImageUpload] Parameter extraction failed: ${extractionResult.error}`);
            console.error(`[FormikImageUpload] Using original URL: ${urlField.value}`);
          }

          if (url) {
            setSignedUrl(url);

            // Test if the URL is valid by creating an Image object
            const img = new Image();
            img.onload = () => {
              debugLog('Signed URL image loaded successfully');
              setImageLoading(false);
            };
            img.onerror = (e) => {
              console.error('[FormikImageUpload] Error loading image from URL:', url);
              setImageLoading(false);
              setSignedUrl(defaultAvatar);
            };
            img.src = url;
          } else {
            console.error('[FormikImageUpload] Received null or empty signed URL');
            setImageLoading(false);
            setSignedUrl(defaultAvatar);
          }
        } catch (error) {
          console.error('[FormikImageUpload] Error in fetchSignedUrl:', error);
          setImageLoading(false);
          setSignedUrl(defaultAvatar);
        }
      } else {
        debugLog('Not an S3 URL, no need to fetch signed URL');
        setSignedUrl(null);
      }
    };

    fetchSignedUrl();
  }, [userId, urlField.value, flagField.value, fetchAttempted, resourceType]);

  // Validate file before processing
  const validateFile = useCallback((file) => {
    // Reset error message
    setErrorMessage('');

    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      setErrorMessage('Please select a valid image file (JPEG, PNG, etc.)');
      return false;
    }

    // Check file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      setErrorMessage(`File is too large. Maximum size is 5MB. Current size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
      return false;
    }

    return true;
  }, []);

  // Handle file drop/selection
  const handleImageChange = useCallback((file, url) => {
    if (file) {
      // Validate file
      if (!validateFile(file)) {
        return;
      }

      // Sanitize the filename
      const sanitizedFileName = ensureSafeFilename(file.name);

      // Update state with the new file
      setImageFile(file);
      setPreviewUrl(url);
      setFileName(sanitizedFileName);

      // Store the file in its own field
      fileHelpers.setValue(file);

      // Set the flag to indicate we have a new image
      flagHelpers.setValue(true);

      // Keep the original URL (we'll use it if upload fails)
      // No need to update urlField here as it will be updated after upload

      // Mark fields as touched
      urlHelpers.setTouched(true);
      fileHelpers.setTouched(true);
      flagHelpers.setTouched(true);

      // Force the form to recognize it's dirty by setting a field value
      formik.setFieldValue('_formDirty', Date.now());

      // Force the form to recognize that a change has been made
      if (formik.setFieldValue) {
        formik.setFieldValue('_forceUpdate', Date.now());
      }
    }
  }, [fileHelpers, flagHelpers, urlHelpers, formik, validateFile]);

  // Clear the image and reset state
  const handleClear = useCallback(() => {
    // If there's a blob URL, revoke it
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }

    // Clear component state
    setImageFile(null);
    setPreviewUrl(null);
    setFileName('');
    setErrorMessage('');

    // Clear form fields
    fileHelpers.setValue(null);
    flagHelpers.setValue(false);

    // Mark fields as touched
    urlHelpers.setTouched(true);
    fileHelpers.setTouched(true);
    flagHelpers.setTouched(true);
  }, [fileHelpers, flagHelpers, urlHelpers, previewUrl]);

  // Handle file selection via click
  const handleImageClick = useCallback(() => {
    if (disabled) return;

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      if (e.target.files && e.target.files[0]) {
        const file = e.target.files[0];
        handleImageChange(file, URL.createObjectURL(file));
      }
    };
    input.click();
  }, [handleImageChange, disabled]);

  // Handle keyboard events for accessibility
  const handleKeyDown = useCallback((e) => {
    if (disabled) return;

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleImageClick();
    }
  }, [handleImageClick, disabled]);

  // Memoize the displayImage calculation to prevent unnecessary re-renders
  const displayImage = useMemo(() => {
    debugLog('displayImage calculation - previewUrl:', previewUrl);
    debugLog('displayImage calculation - urlField.value:', urlField.value);
    debugLog('displayImage calculation - signedUrl:', signedUrl);
    debugLog('displayImage calculation - imageFile:', imageFile);
    debugLog('displayImage calculation - hasNewImageFile:', flagField.value);

    // If we have a new file selected (flag is true), use the preview URL
    if (flagField.value && previewUrl) {
      debugLog('Using previewUrl for newly selected file');
      return previewUrl;
    }

    // If we have a signed URL from S3 and no new file, use it
    if (signedUrl && !flagField.value) {
      debugLog('Using signed URL for display', signedUrl);
      return signedUrl;
    }

    // If we have a URL field value and no new file, use it
    if (urlField.value && typeof urlField.value === 'string' && urlField.value.trim() !== '' && !flagField.value) {
      debugLog('Using urlField.value directly for display');
      return urlField.value;
    }

    // Default to the avatar
    debugLog('Using default avatar for display');
    return defaultAvatar;
  }, [previewUrl, urlField.value, signedUrl, imageFile, flagField.value]);

  // Memoize the hasValue calculation to prevent unnecessary re-renders
  const hasValue = useMemo(() => {
    debugLog('hasValue calculation - imageFile:', imageFile);
    debugLog('hasValue calculation - previewUrl:', previewUrl);
    debugLog('hasValue calculation - urlField.value:', urlField.value);
    debugLog('hasValue calculation - flagField.value:', flagField.value);

    // Check if we have a new image file
    if (flagField.value && fileField.value) {
      return true;
    }

    // Check if we have a preview URL
    if (previewUrl) {
      return true;
    }

    // Check if we have a URL field value
    if (urlField.value && typeof urlField.value === 'string' && urlField.value.trim() !== '') {
      return true;
    }

    return false;
  }, [fileField.value, flagField.value, previewUrl, urlField.value, imageFile]);

  return (
    <Grid.Row className="noTopBottomPad">
      <Grid.Column width={16}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5em' }}>
              <label style={{ display: 'block', margin: 0 }}>
                {label}
                {/* Only show errors specific to this field */}
                {hasError && (
                  <Popup
                    content={errors[urlFieldName] || errors[fileFieldName] || errors[flagFieldName] || errorMessage}
                    trigger={
                      <Icon
                        name="exclamation circle"
                        color="red"
                        style={{ marginLeft: '0.5em', cursor: 'pointer' }}
                      />
                    }
                    position="top center"
                    size="tiny"
                  />
                )}
              </label>
              {hasValue && !disabled && (
                <Button
                  size="mini"
                  compact
                  icon="remove"
                  content="Clear"
                  onClick={handleClear}
                  style={{ marginLeft: '10px', padding: '4px 8px', fontSize: '0.8em' }}
                />
              )}
            </div>
            <ReadOnlyField
              value={fileName}
              error={hasError ? (errors[urlFieldName] || errors[fileFieldName] || errors[flagFieldName] || errorMessage) : null}
              {...props}
            />
            {errorMessage && (
              <div style={{ color: 'red', marginTop: '5px', fontSize: '0.9em' }}>
                {errorMessage}
              </div>
            )}
          </div>
          <div style={{ marginLeft: '20px' }}>
            {/* Custom square image wrapper with cropping */}
            <div
              role="button"
              aria-label="Upload profile picture"
              tabIndex={disabled ? -1 : 0}
              style={{
                width: '100px',
                height: '100px',
                overflow: 'hidden',
                borderRadius: '4px',
                position: 'relative',
                border: '1px solid rgba(34,36,38,.15)',
                backgroundColor: '#f9f9f9',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.7 : 1,
              }}
              onClick={handleImageClick}
              onKeyDown={handleKeyDown}
              onDragOver={(e) => {
                if (disabled) return;
                e.preventDefault();
                e.stopPropagation();
              }}
              onDrop={(e) => {
                if (disabled) return;
                e.preventDefault();
                e.stopPropagation();
                if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                  const file = e.dataTransfer.files[0];
                  handleImageChange(file, URL.createObjectURL(file));
                }
              }}
              title={disabled ? "Image upload disabled" : "Click or drag to upload profile picture"}
            >
              <img
                src={displayImage}
                alt="Profile"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center',
                  opacity: imageLoading ? 0.5 : 1,
                  transition: 'opacity 0.3s'
                }}
                onLoad={() => {
                  debugLog('Signed URL image loaded successfully');
                  setImageLoading(false);
                }}
                onError={(e) => {
                  debugLog('Error loading image');
                  setImageLoading(false);
                  e.target.src = defaultAvatar;
                }}
              />
              {imageLoading && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)'
                }}>
                  <Icon name="spinner" loading />
                </div>
              )}
              <div
                style={{
                  position: 'absolute',
                  bottom: '0px',
                  left: '0',
                  right: '0',
                  textAlign: 'center',
                  fontSize: '10px',
                  color: 'white',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  padding: '2px 0',
                }}
              >
                Profile Picture
              </div>
            </div>
          </div>
        </div>
      </Grid.Column>
    </Grid.Row>
  );
};

// PropTypes validation
FormikImageUpload.propTypes = {
  urlFieldName: PropTypes.string,
  fileFieldName: PropTypes.string,
  flagFieldName: PropTypes.string,
  resourceType: PropTypes.string.isRequired, // Required prop
  label: PropTypes.string,
  disabled: PropTypes.bool
};

// Optimize re-renders with custom comparison function
export default memo(FormikImageUpload, (prevProps, nextProps) => {
  // Only re-render if these props change
  return (
    prevProps.urlFieldName === nextProps.urlFieldName &&
    prevProps.fileFieldName === nextProps.fileFieldName &&
    prevProps.flagFieldName === nextProps.flagFieldName &&
    prevProps.resourceType === nextProps.resourceType &&
    prevProps.label === nextProps.label &&
    prevProps.disabled === nextProps.disabled
  );
});
